import type { StreamServer } from '../types';
import { assessRegionCompatibility, detectRegion } from './regionDetection';
import { countryLabel } from './countryLabels';

/**
 * When the server requires a country, returns a user-facing reason to block the connection.
 * Uses layered public-IP hints + confidence — never claims a verified real-world country.
 * Returns `null` when the check passes or restriction is off.
 */
export async function getConnectionCountryBlockReason(server: StreamServer): Promise<string | null> {
  if (!server.restrictConnectionToCountry || !server.connectionCountryCode?.trim()) {
    return null;
  }
  const expected = server.connectionCountryCode.trim().toUpperCase();
  const expectedName = countryLabel(expected);

  const ac = new AbortController();
  const t = globalThis.setTimeout(() => ac.abort(), 8000);
  try {
    const detected = await detectRegion(ac.signal);
    if (detected.lookupError) {
      const a = assessRegionCompatibility(detected, expected);
      return `${detected.lookupError} ${a.body} Connection blocked: this server asks for ${expectedName} (best-effort public-IP check only).`;
    }

    const assessment = assessRegionCompatibility(detected, expected);

    if (detected.countryCode && detected.countryCode === expected) {
      return null;
    }

    if (!detected.countryCode) {
      return `${assessment.headline}. ${assessment.body} Connection blocked: this server asks for traffic from ${expectedName}.`;
    }

    const prefix =
      assessment.verdict === 'unknown_low_confidence'
        ? `${assessment.headline} — ${assessment.body}`
        : `${assessment.headline}. ${assessment.body}`;

    return `${prefix} Connection blocked: this server asks for ${expectedName}.`;
  } finally {
    globalThis.clearTimeout(t);
  }
}
